/**
 * CriteriaPanel — R2 criteria list container (v38 requirements-analysis
 * `#aiCriteriaList`, lines 1749-1755): one `CriterionRow` per criterion and an
 * "Add criterion" button.
 *
 * Fully controlled — the panel owns no persistent state of its own. Every
 * edit (text, add, remove) builds the next `Criterion[]` and hands it to
 * `onChange`; the caller (`AiReviewPage`, sub-task 2.5/2.6) owns the array
 * plus any stale-run bookkeeping (R3) that follows an edit.
 *
 * In `readOnly` mode it is the SAME list with nothing to operate: one criterion
 * per line, each field read-only, no add, no remove. That is Trello #711 cl.5's
 * "shown read-only with the same component as the form" — `ImportDetailPage`
 * renders a finished review's criteria through this, so the two views cannot
 * drift. One list, not a second render branch: a separate tree is something
 * for React to swap to, and every row in it a fresh mount (PRD
 * `2026-10-06-ai-review-context-prompt-viewer`, C2).
 *
 * The equal-weight helper line ("All criteria are weighted equally.") was
 * REMOVED on 2026-08-25 at Jared's request. Weights are not a concept this UI
 * exposes — there is no weight control anywhere, and the client sends
 * `weight_raw: 1` for every criterion — so the sentence introduced the idea
 * only to dismiss it.
 */
import { Button } from "@nu-assist/ui";
import { Plus } from "lucide-react";
import { useEffect, useRef } from "react";

import { CriterionRow } from "./CriterionRow";
import { nextCriterionId } from "./criteriaRows";

export interface Criterion {
  id: string;
  text: string;
}

/**
 * A DISCRIMINATED UNION on `readOnly`, not one shape with optional callbacks.
 *
 * Making `onChange` merely optional (the first cut of this branch) bought
 * read-only mode at the cost of the editable path's compile-time guarantee:
 * every caller became free to omit the handler, so a controlled panel wired to
 * nothing would type-check and silently swallow edits. `/pr-review` iter 1
 * (Agent C — I6) called it, and it is worth the extra type: the editable panel
 * is the one that has always needed `onChange` most.
 */
interface CriteriaPanelBaseProps {
  criteria: Criterion[];
  /** Max criteria allowed — mirrors the backend `criteria` cap. "Add
   *  criterion" disables at this count so a run never 422s. Default 8. */
  maxCriteria?: number;
}

interface EditableCriteriaPanelProps extends CriteriaPanelBaseProps {
  readOnly?: false;
  /** Required on the editable path — a controlled panel with no handler is a
   *  panel that drops every edit. */
  onChange: (criteria: Criterion[]) => void;
}

interface ReadOnlyCriteriaPanelProps extends CriteriaPanelBaseProps {
  /** Display a finished review's criteria instead of editing a draft's. */
  readOnly: true;
  /** Not accepted: nothing in this mode can produce a change, so a handler
   *  here would be dead code that reads as live wiring. */
  onChange?: never;
}

export type CriteriaPanelProps = EditableCriteriaPanelProps | ReadOnlyCriteriaPanelProps;

export function CriteriaPanel({
  criteria,
  onChange,
  maxCriteria = 8,
  readOnly = false,
}: CriteriaPanelProps) {
  const atMax = criteria.length >= maxCriteria;
  const panelRef = useRef<HTMLDivElement>(null);
  /** Where focus goes once the caller hands back the edited list: a row index,
   *  or the Add button. Without it, a removed row (or an Add that disables
   *  itself at the cap) drops focus to <body>. Kept WITH the list it was
   *  computed for, and used only if that very list comes back as `criteria`:
   *  a caller that drops or rewrites the edit must not leave a target armed
   *  for the next unrelated change (a prefill landing) to fire (PR #1016
   *  iter 2, A §2.1 / C M-C). Any criteria change disarms it. */
  const focusAfterRef = useRef<{ list: Criterion[]; target: number | "add" } | null>(null);

  useEffect(() => {
    const pending = focusAfterRef.current;
    focusAfterRef.current = null;
    if (pending === null || pending.list !== criteria) return;
    const panel = panelRef.current;
    if (!panel) return;
    const { target } = pending;
    const id = target === "add" ? null : criteria[target]?.id;
    const selector = id ? `[data-testid="criterion-text-${id}"]` : '[data-testid="add-criterion"]';
    panel.querySelector<HTMLElement>(selector)?.focus();
  }, [criteria]);

  // `onChange?.` rather than `onChange(` because the union is narrowed by
  // `readOnly` at the CALL site, not inside the body — these three only ever
  // run on the editable rows below, where the type guarantees it exists.
  const handleTextChange = (id: string, text: string) => {
    onChange?.(criteria.map((c) => (c.id === id ? { ...c, text } : c)));
  };

  const handleRemove = (id: string) => {
    const index = criteria.findIndex((c) => c.id === id);
    const next = criteria.filter((c) => c.id !== id);
    // The next row (now at the same index), else the previous, else Add.
    const target = next.length === 0 ? "add" : Math.min(index, next.length - 1);
    focusAfterRef.current = { list: next, target };
    onChange?.(next);
  };

  const handleAdd = () => {
    if (atMax) return;
    // The new row's field — which also keeps focus when this Add disables.
    const next = [...criteria, { id: nextCriterionId(), text: "" }];
    focusAfterRef.current = { list: next, target: criteria.length };
    onChange?.(next);
  };

  return (
    <div ref={panelRef} className="flex flex-col gap-2" data-testid="criteria-panel">
      <div className="flex flex-col gap-2" data-testid="criteria-list">
        {/* The same component, key and position in both modes, so toggling
            `readOnly` updates each row in place. */}
        {criteria.map((criterion, i) =>
          readOnly ? (
            <CriterionRow
              key={criterion.id}
              id={criterion.id}
              text={criterion.text}
              position={i + 1}
              readOnly
            />
          ) : (
            <CriterionRow
              key={criterion.id}
              id={criterion.id}
              text={criterion.text}
              position={i + 1}
              onTextChange={handleTextChange}
              onRemove={handleRemove}
            />
          )
        )}
      </div>

      {readOnly ? null : (
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            onClick={handleAdd}
            disabled={atMax}
            data-testid="add-criterion"
            className="self-start"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            Add criterion
          </Button>
          {atMax ? (
            <span className="text-caption text-text-muted" data-testid="criteria-max-note">
              Maximum {maxCriteria} criteria
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}
